import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote, Modal, Pill, formatQty, typedQty } from '../components/ui.jsx';
import ItemPicker from '../components/ItemPicker.jsx';

/**
 * Req #3 + R7: a check-up of one location, reviewed before it touches anything.
 *
 * The order of the sections is the order the answer is worth reading in. Seen
 * first, because confirming what is still there is the whole point and needs no
 * decision. Then what is new. Then the "maybes" — detections the model was not
 * sure enough about to stage, which do nothing unless somebody taps Include.
 * Last, and still opt-in, the things inventory expected and the photo did not
 * show: a jar behind the milk is invisible to a camera and still in the fridge.
 *
 * The server decides which bucket a row is in, so the confidence line lives in
 * exactly one place.
 */
export default function ScanReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const scan = useApi(`/scans/${id}`);
  const locations = useApi('/locations');
  const [editing, setEditing] = useState(null);
  const [removeMissing, setRemoveMissing] = useState(false);
  const [locationId, setLocationId] = useState('');
  const { busy, error, run } = useAction();

  if (scan.loading && !scan.data) return <Loading rows={5} />;
  if (scan.error) return <ErrorNote error={scan.error} onRetry={scan.reload} />;
  if (!scan.data) return null;

  const data = scan.data;
  const applied = data.status === 'applied';
  const inBucket = (name) => data.items.filter((item) => item.bucket === name);
  const seen = inBucket('verified');
  const additions = inBucket('added');
  const maybes = inBucket('maybe');
  const missing = inBucket('missing');
  const effectiveLocation = locationId || data.location_id;

  const setAction = (item, action) => run(async () => {
    await api.patch(`/scans/${id}/items/${item.id}`, { action });
    scan.reload();
  });

  const apply = () => run(async () => {
    const summary = await api.post(`/scans/${id}/apply`, {
      remove_missing: removeMissing,
      location_id: effectiveLocation || undefined,
    });
    navigate('/inventory', { state: { flash: { ...summary, checkup: data.location_name || null } } });
  });

  const Row = ({ item, showConfidence, action }) => (
    <div className="list-item">
      <button
        type="button"
        className="ghost grow"
        style={{ justifyContent: 'flex-start', textAlign: 'left', padding: 0, minHeight: 0 }}
        onClick={() => !applied && setEditing(item)}
        disabled={applied}
      >
        <span className="grow" style={{ minWidth: 0 }}>
          <span className="truncate" style={{ display: 'block' }}>
            {item.matched_name || item.detected_name}
          </span>
          <span className="tiny muted truncate" style={{ display: 'block' }}>
            {formatQty(item.quantity, item.unit)}
            {!item.matched_name && ' · will be created'}
            {/* Only when the number actually moves. "2 each · was 2 each" on
                every confirmed row is noise that hides the one that changed. */}
            {item.current_quantity != null && item.current_quantity !== item.quantity
              && ` · was ${formatQty(item.current_quantity, item.current_unit)}`}
          </span>
        </span>
      </button>
      {showConfidence && item.confidence != null && (
        <Pill tone={item.confidence >= 0.8 ? 'ok' : item.confidence >= 0.55 ? 'warn' : 'danger'}>
          {Math.round(item.confidence * 100)}%
        </Pill>
      )}
      {!applied && action}
    </div>
  );

  const skip = (item) => (
    <button
      type="button"
      className="icon ghost"
      aria-label="Skip this item"
      onClick={() => setAction(item, 'ignore')}
    >
      ✕
    </button>
  );

  return (
    <div className="stack">
      <ErrorNote error={error} />

      <div>
        <h1>Check-up{data.location_name ? ` · ${data.location_name}` : ''}</h1>
      </div>

      <img src={data.image_url} alt="Shelf check-up" className="thumb-lg" />

      {(data.obscured_areas || missing.length > 0) && (
        <div className="alert warn">
          <strong>The photo didn&rsquo;t show everything.</strong>
          <div className="small" style={{ marginTop: 4 }}>
            {missing.length > 0 && data.expected_here > 0 && (
              <>
                It didn&rsquo;t show {missing.length} of the {data.expected_here} item
                {data.expected_here === 1 ? '' : 's'} expected here.{data.obscured_areas ? ' ' : ''}
              </>
            )}
            {data.obscured_areas && <>The model noted: &ldquo;{data.obscured_areas}&rdquo;</>}
          </div>
        </div>
      )}

      {applied && <div className="alert info">This check-up has already been saved.</div>}

      {seen.length > 0 && (
        <section>
          <div className="section-title">
            <h2>Seen</h2>
            <span className="count">{seen.length}</span>
          </div>
          <div className="card list">
            {seen.map((item) => <Row key={item.id} item={item} showConfidence action={skip(item)} />)}
          </div>
          <p className="tiny muted" style={{ marginTop: 8 }}>
            Still here. Saving marks them checked today.
          </p>
        </section>
      )}

      {additions.length > 0 && (
        <section>
          <div className="section-title">
            <h2>New here</h2>
            <span className="count">{additions.length}</span>
          </div>
          <div className="card list">
            {additions.map((item) => <Row key={item.id} item={item} showConfidence action={skip(item)} />)}
          </div>
        </section>
      )}

      {maybes.length > 0 && (
        <section>
          <div className="section-title">
            <h2>Maybe</h2>
            <span className="count">{maybes.length}</span>
          </div>
          <div className="card list">
            {maybes.map((item) => (
              <Row
                key={item.id}
                item={item}
                showConfidence
                action={(
                  <button type="button" className="small" onClick={() => setAction(item, 'add')}>
                    Include
                  </button>
                )}
              />
            ))}
          </div>
          <p className="tiny muted" style={{ marginTop: 8 }}>
            The camera wasn&rsquo;t sure about these, so they stay out of the pantry unless you
            include them.
          </p>
        </section>
      )}

      {missing.length > 0 && (
        <section>
          <div className="section-title">
            <h2>Not seen</h2>
            <span className="count">{missing.length}</span>
          </div>
          <div className="card list">
            {missing.map((item) => <Row key={item.id} item={item} />)}
          </div>
          <p className="tiny muted" style={{ marginTop: 8 }}>
            Tracked in this location but not in the photo. They may simply be behind something.
          </p>
        </section>
      )}

      {!applied && (
        <div className="card stack">
          {!data.location_id && (
            <label>
              <span className="label-text">Which location is this?</span>
              <select value={locationId} onChange={(event) => setLocationId(event.target.value)}>
                <option value="">Choose one…</option>
                {(locations.data || []).map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
            </label>
          )}

          {missing.length > 0 && (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={removeMissing}
                onChange={(event) => setRemoveMissing(event.target.checked)}
              />
              <span>Also remove the {missing.length} item{missing.length === 1 ? '' : 's'} I couldn&rsquo;t see</span>
            </label>
          )}

          <button
            type="button"
            className="primary full"
            disabled={busy || !effectiveLocation}
            onClick={apply}
          >
            {busy ? <><span className="spinner" /> Saving</> : 'Save check-up'}
          </button>
          {!effectiveLocation && <p className="tiny muted center" style={{ margin: 0 }}>Pick a location first.</p>}
        </div>
      )}

      {editing && (
        <EditScanItem
          scanId={id}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); scan.reload(); }}
        />
      )}
    </div>
  );
}

function EditScanItem({ scanId, item, onClose, onSaved }) {
  const [picked, setPicked] = useState(
    item.master_item_id ? { id: item.master_item_id, name: item.matched_name } : null,
  );
  const [quantity, setQuantity] = useState(item.quantity);
  const [unit, setUnit] = useState(item.unit);
  const { busy, error, run } = useAction();

  const save = () => run(async () => {
    await api.patch(`/scans/${scanId}/items/${item.id}`, {
      master_item_id: picked?.id ?? null,
      // typedQty: a blanked field means "leave it", never Number('') === 0.
      quantity: typedQty(quantity),
      unit,
    });
    onSaved();
  });

  return (
    <Modal title={item.detected_name} onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        <div className="card" style={{ background: 'var(--surface-2)' }}>
          <div className="tiny muted">Seen in the photo</div>
          <div>{item.detected_name}</div>
          {item.confidence != null && (
            <div className="tiny muted">confidence {Math.round(item.confidence * 100)}%</div>
          )}
        </div>

        <label>
          <span className="label-text">Match to</span>
          <ItemPicker value={picked} onChange={setPicked} />
        </label>

        {item.suggestions?.length > 0 && !picked && (
          <div className="chips">
            {item.suggestions.map((suggestion) => (
              <button key={suggestion.id} type="button" className="chip" onClick={() => setPicked(suggestion)}>
                {suggestion.name}
              </button>
            ))}
          </div>
        )}

        <div className="field-row">
          <label>
            <span className="label-text">Quantity</span>
            <input type="number" inputMode="decimal" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </label>
          <label>
            <span className="label-text">Unit</span>
            <input value={unit} onChange={(e) => setUnit(e.target.value)} />
          </label>
        </div>

        <button type="button" className="primary full" disabled={busy} onClick={save}>
          {busy ? <><span className="spinner" /> Saving</> : 'Save'}
        </button>
      </div>
    </Modal>
  );
}
